/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Source/ServiceEndpointsSource" />
import { ServiceEndpointTypes } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { OAuthConfiguration, OAuthConfigurationActionFilter, OAuthConfigurationParams, ServiceEndpoint, ServiceEndpointExecutionRecord, ServiceEndpointProjectReference, ServiceEndpointRequest, ServiceEndpointRequestResult, ServiceEndpointType } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { IServiceEndpointRestClient } from "ServiceEndpoints/Client/RestClient/ServiceEndpoint";

import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { getTFSPageData } from "Tfs/Platform/Page";
import * as Utils_String from "VSS/Core/Util/String";
import { IHtmlNormalizerService } from "VSS/Features/Markdown/Interfaces/IHtmlNormalizerService";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

export interface IServiceEndpointDetails {
    serviceEndpoint: ServiceEndpoint;
    serviceEndpointExecutionHistory: ServiceEndpointExecutionRecord[];
    serviceEndpointType: ServiceEndpointType;
}

export class ServiceEndpointsSource {

    public static getInstance(pageContext: IVssPageContext) {
        if (!ServiceEndpointsSource._instance) {
            ServiceEndpointsSource._instance = new ServiceEndpointsSource(pageContext);
        }
        return ServiceEndpointsSource._instance;
    }

    private constructor(
        private _pageContext: IVssPageContext
    ) {
        this._serviceEndpointRestClient = this._pageContext.getRestClient<IServiceEndpointRestClient>("ServiceEndpoint.IServiceEndpointRestClient");
        const tfsPageData = getTFSPageData(this._pageContext);
        const contributionService = this._pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.getContributionAsync("ms.vss-features.markdown").then(() => {
            this._htmlNormalizerService = this._pageContext.getService<IHtmlNormalizerService>("IHtmlNormalizerService");
        });

        this._serviceEndpointTypesCache = {};
        if (tfsPageData && tfsPageData.project) {
            this._projectId = tfsPageData.project.id;
            this._projectName = tfsPageData.project.name;
        }
    }

    public getServiceEndpointType(type: string): Promise<ServiceEndpointType[]> {
        if (!this._serviceEndpointTypesCache[type]) {
            this._serviceEndpointTypesCache[type] = this._getSupportedServiceEndpointType(type);
        }

        return this._serviceEndpointTypesCache[type];
    }

    public createServiceEndpoint(endpoint: ServiceEndpoint): Promise<ServiceEndpoint> {
        let serviceEndpointProejectReferences: ServiceEndpointProjectReference[] = [];
        serviceEndpointProejectReferences.push({ description: endpoint.description, name: endpoint.name, projectReference: ({ id: this._projectId, name: this._projectName }) });
        endpoint.serviceEndpointProjectReferences = serviceEndpointProejectReferences;
        return this._serviceEndpointRestClient.createServiceEndpoint(endpoint);
    }

    public verifyServiceEndpoint(endpointRequest: ServiceEndpointRequest, endpointId = ""): Promise<ServiceEndpointRequestResult> {
        return this._serviceEndpointRestClient.executeServiceEndpointRequest(endpointRequest, this._projectId, endpointId);
    }

    public getServiceEndpoint(endpointId: string): Promise<ServiceEndpoint> {
        return this._serviceEndpointRestClient.getServiceEndpointDetails(this._projectId, endpointId);
    }

    public updateServiceEndpoint(endpoint: ServiceEndpoint): Promise<ServiceEndpoint> {
        // Since, in update flow, UI is supposed to update the project level values, we are moving the endpoint name and
        // description coming from input to current project reference and clearing the collection level name and description
        let currentProjectReference: ServiceEndpointProjectReference = {
            description: endpoint.description,
            name: endpoint.name,
            projectReference: {
                id: this._projectId,
                name: this._projectName
            }
        };
        endpoint.serviceEndpointProjectReferences = [currentProjectReference];

        endpoint.name = null;
        endpoint.description = null;

        return this._serviceEndpointRestClient.updateServiceEndpoint(endpoint, endpoint.id, "");
    }

    public deleteServiceEndpoint(endpointId: string): Promise<void> {
        let projectIds: string[] = [];
        projectIds.push(this._projectId);
        return this._serviceEndpointRestClient.deleteServiceEndpoint(endpointId, projectIds);
    }

    public normalizeHTML(content: string): string {
        if (!this._htmlNormalizerService) {
            setTimeout(() => {
                if (this._htmlNormalizerService) {
                    return this._htmlNormalizerService.normalizeStripAttributes(content);
                } else {
                    return "";
                }
            }, 0);
        } else {
            return this._htmlNormalizerService.normalizeStripAttributes(content);
        }
    }

    public getOAuthConfigurations(endpointType: ServiceEndpointType): Promise<OAuthConfiguration[]> {
        return new Promise((resolve, reject) => {
            if (endpointType.authenticationSchemes) {
                let index = endpointType.authenticationSchemes.findIndex((authScheme) => {
                    return (authScheme.scheme === Constants.OAuth2Scheme);
                });
            
                if (index < 0) {
                    resolve([]);
                }
            }

            this._serviceEndpointRestClient.getOAuthConfigurations(endpointType.name, OAuthConfigurationActionFilter.None).then((configurations: OAuthConfiguration[]) => {
                resolve(configurations);
            });
        });
    }

    public createOAuthConfigurations(params: OAuthConfigurationParams): Promise<OAuthConfiguration> {
        return new Promise((resolve, reject) => {
            this._serviceEndpointRestClient.createOAuthConfiguration(params).then((configuration: OAuthConfiguration) => {
                resolve(configuration);
            }).catch((error: Error) => {
                reject(error.message);
            });
        });
    }

    public updateOAuthConfigurations(params: OAuthConfigurationParams, configurationId: string): Promise<OAuthConfiguration> {
        return new Promise((resolve, reject) => {
            this._serviceEndpointRestClient.updateOAuthConfiguration(params, configurationId).then((configuration: OAuthConfiguration) => {
                resolve(configuration);
            }).catch((error: Error) => {
                reject(error.message);
            });
        });
    }

    public deleteOAuthConfiguration(configurationId: string): Promise<OAuthConfiguration> {
        return new Promise((resolve, reject) => {
            this._serviceEndpointRestClient.deleteOAuthConfiguration(configurationId).then((configuration: OAuthConfiguration) => {
                resolve(configuration);
            }).catch((error: Error) => {
                reject(error.message);
            });
        });
    }

    public _getSupportedServiceEndpointType(type: string): Promise<ServiceEndpointType[]> {
        return new Promise((resolve, reject) => {
            this._serviceEndpointRestClient.getServiceEndpointTypes(type).then((result: ServiceEndpointType[]) => {
                if (result) {
                    result.forEach((endpointType: ServiceEndpointType) => {
                        if (Utils_String.equals(endpointType.name, ServiceEndpointTypes.GitHub, true)
                            && endpointType.authenticationSchemes) {
                            let filteredAuthSchemes = endpointType.authenticationSchemes.filter((authScheme) => {
                                return !Utils_String.equals(authScheme.scheme, Constants.InstallationToken, true);
                            });

                            endpointType.authenticationSchemes = filteredAuthSchemes;
                        }
                    });
                }

                resolve(result);
            }, reject);
        });
    }

    private _serviceEndpointRestClient: IServiceEndpointRestClient;
    private _projectId: string;
    private _projectName: string;
    private _serviceEndpointTypesCache: { [key: string]: Promise<ServiceEndpointType[]> };
    private static _instance: ServiceEndpointsSource = null;
    private _htmlNormalizerService: IHtmlNormalizerService;
}
